import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Gated pipeline stages — a note is REQUIRED before the case can
 * advance to any of these. The note is passed to `POST /debt/cases/
 * {id}/stage` as `reason` so it lands in the audit trail (`debt_
 * pipeline_events.meta.reason`) and Finance/Head can reconcile later.
 *
 * Keep this table in one file so the Kanban "Advance" chip and the
 * case-detail "Advance → next" button surface the exact same prompt.
 */
export const GATED_STAGES = {
  "Mandate Signed": {
    label: "E-sign envelope reference",
    placeholder: "e.g. VZ-DBT-MND-CS-000123 · signed by all parties on 2026-08-17",
    hint: "The mandate envelope ID makes it easy to pull the signed PDF from the vault later.",
  },
  "Sanctioned": {
    label: "Sanction letter reference + key terms",
    placeholder: "e.g. HDFC/CORP/2026/00821 · ₹2.5 Cr · ROI 11.25% · Tenure 24m · PF 1%",
    hint: "Copy the sanction letter reference and the key terms. This becomes the audit trail on the case.",
  },
  "Partially Disbursed": {
    label: "Partial disbursement UTR + tranche",
    placeholder: "e.g. NEFT ref N026081712345678 · Tranche 1 of 3 · ₹80 L",
    hint: "Which tranche is this, and what's the UTR? Helps track the remaining draw-downs.",
  },
  "Fully Disbursed": {
    label: "Disbursement UTR / reference",
    placeholder: "e.g. NEFT ref N026081712345678 · ₹2.5 Cr · to IDFC First A/C 5641…",
    hint: "Record the UTR/NEFT/RTGS reference. Finance uses this to reconcile the incentive accrual.",
  },
  "Rejected": {
    label: "Rejection reason",
    placeholder: "e.g. Bureau score below cutoff · Age of business < 3 years · Concentration risk",
    hint: "Be specific. This closes the loop with the borrower and helps CP attribution track win-rates per rejection code.",
  },
  "Dropped": {
    label: "Drop reason",
    placeholder: "e.g. Borrower took a different lender · Went cold · Duplicate lead",
    hint: "Why did we drop this case? Used for funnel diagnostics.",
  },
};

export function isGated(stage) {
  return Boolean(stage && GATED_STAGES[stage]);
}

/**
 * AdvanceGateModal — modal pop-up that collects the mandatory note
 * before a case advances to a gated stage. Renders nothing when
 * `open` is false.
 */
export default function AdvanceGateModal({ open, targetStage, caseUid, onCancel, onSubmit, submitting }) {
  const [note, setNote] = useState("");
  const gate = targetStage ? GATED_STAGES[targetStage] : null;

  // Reset the field whenever the modal reopens on a new stage.
  useEffect(() => { if (open) setNote(""); }, [open, targetStage]);

  if (!open || !gate) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="advance-gate-modal">
      <div className="bg-white rounded-xl max-w-md w-full shadow-[0_20px_60px_rgba(15,23,42,0.35)] border border-navy/10">
        <div className="px-5 py-4 border-b border-ink/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-yellow-400/25 text-[#8A6600] inline-flex items-center justify-center">
              <AlertTriangle size={15} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-navy">Confirm advance · {caseUid || "case"}</div>
              <div className="font-heading text-ink text-sm">Advancing to <span className="text-navy">{targetStage}</span></div>
            </div>
          </div>
          <button type="button" onClick={onCancel} className="text-ink-muted hover:text-ink" aria-label="Close" data-testid="advance-gate-close">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-5">
          <label htmlFor="advance-gate-note" className="block text-xs font-heading text-ink mb-1">{gate.label}</label>
          <textarea
            id="advance-gate-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={gate.placeholder}
            rows={3}
            className="w-full px-3 py-2 border border-navy/15 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-navy"
            data-testid="advance-gate-note"
            autoFocus
          />
          <div className="text-[11px] text-ink-muted mt-2 leading-relaxed">{gate.hint}</div>
        </div>
        <div className="px-5 py-4 border-t border-ink/10 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-heading text-navy hover:bg-navy/5 rounded border border-navy/20"
            data-testid="advance-gate-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(note.trim())}
            disabled={submitting || note.trim().length < 3}
            className="px-4 py-1.5 text-xs font-heading font-semibold text-navy bg-gold rounded hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="advance-gate-submit"
          >
            {submitting ? "Advancing…" : `Advance → ${targetStage}`}
          </button>
        </div>
      </div>
    </div>
  );
}
